import * as React from 'react'
import { useMemo } from 'react'
import type { CreatorCampTemplate } from '../components/TemplateLibraryPage/TemplateLibraryPage'
import {
  GOALS,
  STRUCTURES,
  USER_TYPES,
  type Tag,
  type Template,
  type UserType,
} from '../notion-sync/interface'

// TODO: creator-camp-cleanup
export const isCreatorCampTemplate = (
  template: Template,
): template is CreatorCampTemplate => {
  return template.creatorCampDate !== null
}

const sortByCreatedAt = (a: Template, b: Template): number => {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
}

export const useFilteredTemplates = ({
  templates,
  templateTypeFilter,
  templateFilters,
  // TODO: creator-camp-cleanup
  todayDate,
}: {
  templates: Template[]
  templateTypeFilter: string
  templateFilters: string[]
  // TODO: creator-camp-cleanup
  todayDate: string
}): Array<Template | CreatorCampTemplate> => {
  const filterTemplate = React.useCallback(
    (template: Template) => {
      // Filter based on Template Type
      const showAllTemplateTypes = templateTypeFilter === 'All'
      const templateMatchesTypeFilter =
        // TODO: creator-camp-cleanup
        templateTypeFilter === 'Creator Camp'
          ? template.creatorCampDate !== null
          : template.type === templateTypeFilter

      // Filter based on User Type
      const noUserTypeFiltersSelected = !USER_TYPES.some((userType) =>
        templateFilters?.includes(userType),
      )
      const templateMatchesUserTypeFilter = templateFilters?.some((filter) =>
        template.userTypes.includes(filter as UserType),
      )

      // Filter based on Goal
      const noGoalFiltersSelected = !GOALS.some((goal) =>
        templateFilters?.includes(goal),
      )
      const templateMatchesGoalFilter = templateFilters?.some(
        (filter) => template.goal === filter,
      )

      // Filter based on Structure
      const noStructureFiltersSelected = !STRUCTURES.some((structure) =>
        templateFilters?.includes(structure),
      )
      const templateMatchesStructureFilter = templateFilters?.some(
        (filter) => template.structure === filter,
      )

      return (
        (showAllTemplateTypes || templateMatchesTypeFilter) &&
        (noUserTypeFiltersSelected || templateMatchesUserTypeFilter) &&
        (noGoalFiltersSelected || templateMatchesGoalFilter) &&
        (noStructureFiltersSelected || templateMatchesStructureFilter)
      )
    },
    [templateTypeFilter, templateFilters],
  )

  // Augment templates: add 'New' tag to templates created in the last 7 days
  const newTaggedTemplates = React.useMemo(() => {
    const now = new Date()
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    return templates.map((template) => {
      const createdAtDate = new Date(template.createdAt)
      const isNew = createdAtDate > weekAgo
      // Only add 'New' if not already present
      if (isNew && !template.tags.includes('New')) {
        return { ...template, tags: [...template.tags, 'New' as Tag] }
      }
      return template
    })
  }, [templates])

  const filteredTemplates = useMemo(
    () => newTaggedTemplates.filter(filterTemplate).sort(sortByCreatedAt),
    [newTaggedTemplates, filterTemplate],
  )

  /**
   * Creator Camp Templates
   * This template category is temporary and facilitates Creator Camp
   * running from June 16 to July 15, 2025
   */
  // TODO: creator-camp-cleanup
  const creatorCampTemplates: CreatorCampTemplate[] = useMemo(() => {
    return templates.filter(isCreatorCampTemplate)
  }, [templates])

  // TODO: creator-camp-cleanup
  const sortedFilteredCreatorCampTemplates = useMemo(() => {
    return creatorCampTemplates
      .filter(filterTemplate)
      .filter(
        (template) => new Date(template.creatorCampDate) <= new Date(todayDate),
      )
      .sort(
        (a, b) =>
          new Date(a.creatorCampDate).getTime() -
          new Date(b.creatorCampDate).getTime(),
      )
  }, [creatorCampTemplates, filterTemplate, todayDate])

  return React.useMemo(() => {
    // TODO: creator-camp-cleanup
    return templateTypeFilter === 'Creator Camp'
      ? sortedFilteredCreatorCampTemplates
      : filteredTemplates
  }, [
    filteredTemplates,
    sortedFilteredCreatorCampTemplates,
    templateTypeFilter,
  ])
}
